import React, { useEffect, useRef } from 'react';
import { usePrefersDark } from '../hooks/usePrefersDark';

const FEATURES = [
  { cx:0.22, cy:0.48, sx:0.28, sy:0.18, theta:-0.45, amp: 1.00, rx:0.022, ry:0.016, spx:0.007, spy:0.005, px:0.0, py:0.0 },
  { cx:0.72, cy:0.30, sx:0.22, sy:0.16, theta: 0.40, amp: 0.92, rx:0.018, ry:0.022, spx:0.006, spy:0.008, px:1.8, py:2.4 },
  { cx:0.60, cy:0.70, sx:0.16, sy:0.12, theta:-0.25, amp: 0.65, rx:0.016, ry:0.014, spx:0.009, spy:0.007, px:3.1, py:1.1 },
  { cx:0.88, cy:0.58, sx:0.13, sy:0.10, theta: 0.60, amp: 0.50, rx:0.013, ry:0.011, spx:0.005, spy:0.009, px:0.9, py:3.5 },
  { cx:0.40, cy:0.16, sx:0.10, sy:0.08, theta: 0.10, amp: 0.38, rx:0.011, ry:0.013, spx:0.010, spy:0.006, px:2.2, py:0.7 },
  { cx:0.46, cy:0.60, sx:0.13, sy:0.10, theta:-0.30, amp:-0.44, rx:0.013, ry:0.011, spx:0.008, spy:0.007, px:1.5, py:2.0 },
];

const NUM_LEVELS    = 30;
const MIN_LEVEL     = -0.38;
const MAX_LEVEL     =  1.06;
const CELL          = 9;
const GOLDEN_ANGLE  = 2.39996;   // radians — ensures no two levels share a phase cycle
const UNDULATE_AMP  = 5.5;       // px, max perpendicular displacement

// Marching squares: edge pairs per case (edges: 0=T,1=R,2=B,3=L)
const MS: Array<[number,number][]> = [
  [],[[3,2]],[[2,1]],[[3,1]],
  [[0,1]],[[0,1],[3,2]],[[0,2]],[[3,0]],
  [[3,0]],[[0,2]],[[3,0],[2,1]],[[0,1]],
  [[3,1]],[[2,1]],[[3,2]],[],
];

function interp(a: number, b: number, level: number): number {
  const d = b - a;
  return Math.abs(d) < 1e-6 ? 0.5 : Math.max(0, Math.min(1, (level - a) / d));
}

interface TopoBackgroundProps {
  /** Contour colour as an "r,g,b" string. Defaults to warm cream for dark backdrops. */
  lineColor?: string;
  /** Contour colour while the visitor's device is in dark mode. */
  darkLineColor?: string;
}

const TopoBackground: React.FC<TopoBackgroundProps> = ({ lineColor = '244,226,198', darkLineColor = '92,100,178' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersDark = usePrefersDark();
  const activeColor = prefersDark ? darkLineColor : lineColor;
  const colorRef  = useRef(activeColor);
  colorRef.current = activeColor;
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W = 0, H = 0, GW = 0, GH = 0;
    let heights  = new Float32Array(0);
    let numEdges = 0;

    // Per-edge: adjacency (max degree 2), intersection point, visited flag
    let adjA     = new Int32Array(0);
    let adjCount = new Uint8Array(0);
    let epx      = new Float32Array(0);
    let epy      = new Float32Array(0);
    let visited  = new Uint8Array(0);

    // Pre-allocated flat point buffer for chain tracing (avoids per-chain allocation)
    let ptsBuf   = new Float32Array(0);
    let usedEdges: number[] = [];

    const resize = () => {
      const p = canvas.parentElement;
      W = p ? p.clientWidth  : window.innerWidth;
      H = p ? p.clientHeight : window.innerHeight;
      canvas.width  = W;
      canvas.height = H;
      GW = Math.ceil(W / CELL) + 1;
      GH = Math.ceil(H / CELL) + 1;
      heights = new Float32Array(GW * GH);

      // H-edges: top of cell(gx,gy)  → gy*GW + gx        (gy ∈ 0..GH, gx ∈ 0..GW-1)
      // V-edges: left of cell(gx,gy) → GW*(GH+1) + gy*(GW+1) + gx  (gx ∈ 0..GW)
      numEdges = GW * (GH + 1) + (GW + 1) * GH;
      adjA     = new Int32Array(numEdges * 2).fill(-1);
      adjCount = new Uint8Array(numEdges);
      epx      = new Float32Array(numEdges);
      epy      = new Float32Array(numEdges);
      visited  = new Uint8Array(numEdges);
      ptsBuf   = new Float32Array(numEdges * 2); // worst-case one chain spans all edges
      usedEdges = [];
    };

    resize();
    window.addEventListener('resize', resize);

    const hId = (gx: number, gy: number) => gy * GW + gx;
    const vId = (gx: number, gy: number) => GW * (GH + 1) + gy * (GW + 1) + gx;

    let t = 0;

    const traceAndDraw = (start: number, levelPhase: number) => {
      let len = 0;
      let cur = start;
      let prev = -1;
      let closed = false;

      while (cur !== -1 && !visited[cur]) {
        visited[cur] = 1;
        ptsBuf[len++] = epx[cur];
        ptsBuf[len++] = epy[cur];
        const n0 = adjA[cur*2], n1 = adjA[cur*2+1];
        let next = -1;
        if (n0 !== prev && n0 !== -1) next = n0;
        else if (n1 !== prev && n1 !== -1) next = n1;
        if (next === start) { closed = true; break; }
        prev = cur;
        cur = next;
      }

      const n = len >> 1;
      if (n < 2) return;

      for (let i = 0; i < n; i++) {
        const frac = i / Math.max(1, n - 1);
        const ia = closed ? ((i + n - 1) % n) : Math.max(0, i - 1);
        const ib = closed ? ((i + 1) % n)     : Math.min(n - 1, i + 1);
        const tx = ptsBuf[ib*2]   - ptsBuf[ia*2];
        const ty = ptsBuf[ib*2+1] - ptsBuf[ia*2+1];
        const tlen = Math.sqrt(tx*tx + ty*ty) || 1;
        const nx = -ty / tlen;
        const ny =  tx / tlen;
        const wave = UNDULATE_AMP * Math.sin(frac * 6.0 + t * 0.9 + levelPhase);
        ptsBuf[i*2]   += nx * wave;
        ptsBuf[i*2+1] += ny * wave;
      }

      ctx.moveTo(ptsBuf[0], ptsBuf[1]);
      for (let i = 0; i < n - 1; i++) {
        const i0 = closed ? ((i + n - 1) % n) : Math.max(0, i - 1);
        const i2 = i + 1;
        const i3 = closed ? ((i + 2) % n) : Math.min(n - 1, i + 2);
        const p0x = ptsBuf[i0*2], p0y = ptsBuf[i0*2+1];
        const p1x = ptsBuf[i *2], p1y = ptsBuf[i *2+1];
        const p2x = ptsBuf[i2*2], p2y = ptsBuf[i2*2+1];
        const p3x = ptsBuf[i3*2], p3y = ptsBuf[i3*2+1];
        ctx.bezierCurveTo(
          p1x + (p2x - p0x) / 6, p1y + (p2y - p0y) / 6,
          p2x - (p3x - p1x) / 6, p2y - (p3y - p1y) / 6,
          p2x, p2y,
        );
      }
      if (closed) ctx.closePath();
    };

    const draw = () => {
      if (typeof window.requestAnimationFrame === 'function') {
        rafRef.current = window.requestAnimationFrame(draw);
      }
      t += 0.005;

      const ref = Math.min(W, H);

      // Update height field
      for (let gy = 0; gy < GH; gy++) {
        const worldY = gy * CELL;
        for (let gx = 0; gx < GW; gx++) {
          const worldX = gx * CELL;
          let h = 0;
          for (const f of FEATURES) {
            const cx = (f.cx + f.rx * Math.cos(t * f.spx + f.px)) * W;
            const cy = (f.cy + f.ry * Math.sin(t * f.spy + f.py)) * H;
            const dx = worldX - cx;
            const dy = worldY - cy;
            const cosT = Math.cos(f.theta);
            const sinT = Math.sin(f.theta);
            const ru =  dx * cosT + dy * sinT;
            const rv = -dx * sinT + dy * cosT;
            const su = f.sx * ref;
            const sv = f.sy * ref;
            h += f.amp * Math.exp(-(ru*ru / (2*su*su) + rv*rv / (2*sv*sv)));
          }
          heights[gy * GW + gx] = h;
        }
      }

      ctx.clearRect(0, 0, W, H);
      ctx.lineJoin = 'round';
      ctx.lineCap  = 'round';

      for (let li = 0; li < NUM_LEVELS; li++) {
        const level     = MIN_LEVEL + (MAX_LEVEL - MIN_LEVEL) * li / (NUM_LEVELS - 1);
        const major     = li % 6 === 0;
        const shimmer   = 0.75 + 0.25 * Math.sin(t * 0.4 + li * 0.18);
        // Each level gets a unique phase so they breathe independently
        const levelPhase = li * GOLDEN_ANGLE;

        ctx.lineWidth = major ? 1.8 : 0.9;

        // ── Build edge adjacency for this contour level ──────────────────────
        usedEdges.length = 0;

        for (let gy = 0; gy < GH - 1; gy++) {
          for (let gx = 0; gx < GW - 1; gx++) {
            const tl = heights[ gy    * GW + gx    ];
            const tr = heights[ gy    * GW + gx + 1];
            const bl = heights[(gy+1) * GW + gx    ];
            const br = heights[(gy+1) * GW + gx + 1];

            const ci = ((tl>level)?8:0)|((tr>level)?4:0)|((br>level)?2:0)|((bl>level)?1:0);
            const segs = MS[ci];
            if (!segs.length) continue;

            const x0 = gx * CELL, y0 = gy * CELL;
            const ids = [hId(gx, gy), vId(gx+1, gy), hId(gx, gy+1), vId(gx, gy)];
            const ex  = [
              x0 + CELL * interp(tl, tr, level),
              x0 + CELL,
              x0 + CELL * interp(bl, br, level),
              x0,
            ];
            const ey  = [
              y0,
              y0 + CELL * interp(tr, br, level),
              y0 + CELL,
              y0 + CELL * interp(tl, bl, level),
            ];

            for (const [ea, eb] of segs) {
              const ka = ids[ea], kb = ids[eb];
              if (adjCount[ka] === 0) {
                adjA[ka*2] = adjA[ka*2+1] = -1;
                epx[ka] = ex[ea]; epy[ka] = ey[ea];
                usedEdges.push(ka);
              }
              if (adjCount[kb] === 0) {
                adjA[kb*2] = adjA[kb*2+1] = -1;
                epx[kb] = ex[eb]; epy[kb] = ey[eb];
                usedEdges.push(kb);
              }
              if (adjCount[ka] < 2) adjA[ka*2 + adjCount[ka]++] = kb;
              if (adjCount[kb] < 2) adjA[kb*2 + adjCount[kb]++] = ka;
            }
          }
        }

        // ── Trace chains & draw with Catmull-Rom splines ─────────────────────
        const opacity = (major ? 0.42 : 0.16) * shimmer;
        ctx.strokeStyle = `rgba(${colorRef.current},${opacity.toFixed(3)})`;
        ctx.beginPath();

        // Open chains first (degree-1 endpoints), then closed loops
        for (const eid of usedEdges) {
          if (adjCount[eid] === 1 && !visited[eid]) traceAndDraw(eid, levelPhase);
        }
        for (const eid of usedEdges) {
          if (!visited[eid]) traceAndDraw(eid, levelPhase);
        }

        ctx.stroke();

        // Reset working state for next level
        for (const eid of usedEdges) {
          adjCount[eid] = 0;
          visited[eid]  = 0;
        }
      }
    };

    draw();
    return () => {
      if (rafRef.current && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="topo-background-canvas"
    />
  );
};

export default TopoBackground;
